import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/context/LanguageContext';
import { motion } from 'framer-motion';
import { Ruler, UserCheck, Building2, FileCheck, Package, CalendarCheck, ArrowRight, Search } from 'lucide-react';

const STEPS = [
  { key: 'measurements', icon: Ruler },
  { key: 'techVisit', icon: UserCheck },
  { key: 'showroom', icon: Building2 },
  { key: 'agreement', icon: FileCheck },
  { key: 'production', icon: Package },
  { key: 'installation', icon: CalendarCheck },
];

const OrderJourneySection = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();

  return (
    <section data-testid="order-journey-section" className="section-padding bg-brand-dark text-white grain relative overflow-hidden">
      {/* Decorative background */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 50%, var(--brand-orange) 0%, transparent 50%), radial-gradient(circle at 80% 50%, var(--brand-orange) 0%, transparent 50%)',
        }}
      />

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="label-text mb-4 block">{t('orderJourney.label')}</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            {t('orderJourney.title')}
          </h2>
          <p className="text-white/60 text-lg">{t('orderJourney.description')}</p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const num = t(`orderJourney.steps.${step.key}.num`);
            const title = t(`orderJourney.steps.${step.key}.title`);
            const desc = t(`orderJourney.steps.${step.key}.desc`);
            const isLastInRow = index % 3 === 2;

            return (
              <motion.div
                key={step.key}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (index % 3) * 0.12 }}
                className="relative group"
              >
                {/* Connector */}
                {!isLastInRow && index < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-12 start-full w-full h-px bg-white/8 z-0" />
                )}

                <div className="relative z-10 p-7 border border-white/10 bg-white/4 hover:border-gold/40 hover:bg-white/7 transition-all duration-500 h-full">
                  {/* Step number watermark */}
                  <span className="text-gold text-5xl font-bold opacity-15 absolute top-4 end-5 leading-none">
                    {num}
                  </span>

                  {/* Icon */}
                  <div className="w-14 h-14 bg-gold/10 rounded-full flex items-center justify-center mb-5 group-hover:bg-gold/20 transition-colors">
                    <Icon className="text-gold" size={24} />
                  </div>

                  <h3 className="text-lg font-semibold mb-3">{title}</h3>
                  <p className="text-white/55 leading-relaxed text-sm">{desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/start"
            className="inline-flex items-center gap-3 bg-gold text-charcoal-deep font-bold px-10 py-5 text-lg hover:opacity-90 transition-opacity"
          >
            {t('orderJourney.cta')}
            <ArrowRight size={22} className={currentLang === 'ar' ? 'rotate-180' : ''} />
          </Link>

          <Link
            to="/follow-up"
            className="inline-flex items-center gap-3 border-2 border-white/25 text-white font-semibold px-8 py-[18px] text-base hover:border-gold/60 hover:text-gold hover:bg-gold/5 transition-all duration-300"
          >
            <Search size={18} />
            {t('orderJourney.followUp')}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default OrderJourneySection;
